import { useCallback } from 'react'

import { useParams, useSearchParams } from 'next/navigation'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'

import buyUrlPrefix from '@/buyer-portal/buyUrlPrefix'

type StringWithSlashPrefix = `/${string}`

const useQueryAccountId = () => {
  // The original implementation uses router.query which combines both the
  // search params and dynamic route params. To maintain parity with the
  // original implementation, we need to look for the accountId in both places.
  const urlAccountId = useParams()?.accountId as string | undefined
  const searchParamsAccountId = useSearchParams()?.get('accountId')
  const queryAccountId = urlAccountId ?? searchParamsAccountId

  if (typeof queryAccountId === 'string' && queryAccountId !== 'my') {
    return queryAccountId
  }
  return undefined
}

export const usePrefixHrefWithAccountId = () => {
  const queryAccountId = useQueryAccountId()

  return useCallback(
    ({
      href,
      appendLegacyPrefix,
    }: {
      href: StringWithSlashPrefix
      appendLegacyPrefix?: boolean
    }): StringWithSlashPrefix =>
      queryAccountId
        ? `${appendLegacyPrefix ? buyUrlPrefix : ''}/user/${encodeURIComponent(queryAccountId)}${href}`
        : href,
    [queryAccountId]
  )
}

export function useUserAccountId(accessTokenRequired?: true): string
export function useUserAccountId(accessTokenRequired: false): string | undefined
export function useUserAccountId(accessTokenRequired = true) {
  const queryAccountId = useQueryAccountId()
  const decodedToken = useDecodedAccessToken({ required: accessTokenRequired })

  if (queryAccountId) {
    return queryAccountId
  }
  return decodedToken?.accountId
}
